import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { waAPI, clientsAPI } from '../../services/api';
import { formatDateMY, formatDateWithWeekdayMY } from '../../utils/dateUtils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Loader2,
  Play,
  ExternalLink,
  Check,
  X,
  SkipForward,
  StopCircle,
  Users,
  UserPlus,
  UsersRound,
  Search,
  Calendar,
  AlertTriangle,
  MessageSquare,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Image,
  Trash2,
  RotateCcw,
  Download,
  Copy
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';

const WAFixedFlowDetailPage = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const [flow, setFlow] = useState(null);
  const [queue, setQueue] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('queue');
  const [copiedImageId, setCopiedImageId] = useState(null);
  
  // Entries pagination
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [entriesTotalPages, setEntriesTotalPages] = useState(1);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entrySearchTerm, setEntrySearchTerm] = useState('');
  
  // Add clients modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // Add group modal
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  
  // Image preview modal
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  
  // Message preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  // Summary stats
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [breakdownPeriod, setBreakdownPeriod] = useState('week');
  
  // Queue filter
  const [queueFilter, setQueueFilter] = useState('all');
  const [queuePage, setQueuePage] = useState(1);
  const QUEUE_PAGE_SIZE = 10;

  // Bulk delete
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const getStatusBadge = (item) => {
    if (item.is_overdue) return { label: 'Overdue', color: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle size={12} /> };
    return { label: 'Pending', color: 'bg-blue-100 text-blue-700', icon: <Clock size={12} /> };
  };

  const filteredQueue = queue.filter(item => {
    if (queueFilter === 'all') return true;
    if (queueFilter === 'overdue') return item.is_overdue;
    if (queueFilter === 'pending') return !item.is_overdue;
    return true;
  });

  const totalQueuePages = Math.ceil(filteredQueue.length / QUEUE_PAGE_SIZE);
  const paginatedQueue = filteredQueue.slice((queuePage - 1) * QUEUE_PAGE_SIZE, queuePage * QUEUE_PAGE_SIZE);

  const openPreview = (item) => {
    setPreviewItem(item);
    setShowPreview(true);
  };

  const handleCopyImage = async (imageUrl, itemId) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const img = document.createElement('img');
      img.src = URL.createObjectURL(blob);
      await new Promise(resolve => { img.onload = resolve; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      const pngBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      URL.revokeObjectURL(img.src);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
      setCopiedImageId(itemId);
      setTimeout(() => setCopiedImageId(null), 2000);
    } catch (e) {
      window.open(imageUrl, '_blank');
    }
  };

  useEffect(() => {
    fetchData();
  }, [flowId]);

  useEffect(() => {
    if (activeTab === 'summary') fetchSummary();
    if (activeTab === 'entries') fetchEntries(entriesPage, entriesPerPage, entrySearchTerm);
  }, [activeTab, entriesPage, entriesPerPage]);

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get(`/wa/fixed-flows/${flowId}/summary`);
      setSummary(res.data);
    } catch (e) {} finally {
      setSummaryLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
    if (activeTab === 'entries') fetchEntries(entriesPage, entriesPerPage, entrySearchTerm);
    if (activeTab === 'summary') fetchSummary();
  };

  const fetchData = async () => {
    try {
      const [flowRes, queueRes] = await Promise.all([
        waAPI.getFixedFlow(flowId),
        waAPI.getFixedFlowQueue(flowId)
      ]);
      setFlow(flowRes.data);
      setQueue(queueRes.data);
      setSelectedEntries([]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async (pg, pp, search) => {
    setEntriesLoading(true);
    try {
      const params = { page: pg || entriesPage, per_page: pp || entriesPerPage };
      if (search !== undefined ? search : entrySearchTerm) params.search = search !== undefined ? search : entrySearchTerm;
      const res = await api.get(`/wa/fixed-flows/${flowId}/entries`, { params });
      setEntries(res.data.entries || []);
      setEntriesTotalPages(res.data.total_pages || 1);
      setEntriesTotalCount(res.data.total || 0);
    } catch {}
    setEntriesLoading(false);
  };

  const handleMarkSent = async (entryId) => {
    try {
      await waAPI.markFlowMessageSent(flowId, entryId);
      fetchData();
    } catch (error) {
    }
  };

  const handleSkip = async (entryId, reason = '') => {
    try {
      await waAPI.skipFlowMessage(flowId, entryId, reason);
      fetchData();
    } catch (error) {
    }
  };

  // Skip reason modal
  const [skipReasonModal, setSkipReasonModal] = useState(null); // { entryId }
  const [skipReasonText, setSkipReasonText] = useState('');
  const SKIP_REASONS = [
    'WhatsApp disconnected',
    'Customer not responding',
    'Wrong number',
    'Customer requested to stop',
    'Duplicate contact',
    'Other'
  ];
  const submitSkipReason = () => {
    if (!skipReasonModal) return;
    handleSkip(skipReasonModal.entryId, skipReasonText);
    setSkipReasonModal(null);
    setSkipReasonText('');
  };

  const handleStop = async (entryId) => {
    if (!window.confirm('Stop this flow for this client? They will not receive any more messages from this flow.')) {
      return;
    }
    try {
      await waAPI.stopFlowForClient(flowId, entryId);
      fetchData();
    } catch (error) {
    }
  };

  const handleRetryEntry = async (entryId) => {
    try {
      await api.post(`/wa/fixed-flows/${flowId}/entries/${entryId}/retry`);
      fetchData();
    } catch (error) {
    }
  };

  const handleRetrySkipped = async (entryId) => {
    try {
      await api.post(`/wa/fixed-flows/${flowId}/entries/${entryId}/retry-skipped`);
      fetchData();
    } catch (error) {
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Remove this client from the flow permanently? This cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/wa/fixed-flows/${flowId}/entries/${entryId}`);
      fetchData();
    } catch (error) {
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.length === 0) return;
    if (!window.confirm(`Remove ${selectedEntries.length} client(s) from this flow permanently? This cannot be undone.`)) {
      return;
    }
    setBulkDeleting(true);
    try {
      await api.post(`/wa/fixed-flows/${flowId}/entries/bulk-delete`, { entry_ids: selectedEntries });
      setSelectedEntries([]);
      fetchData();
    } catch (error) {
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const openAddClientsModal = async () => {
    setShowAddModal(true);
    setLoadingClients(true);
    setSelectedClients([]);
    setSearchTerm('');
    
    try {
      // Get clients with pagination (max 100 per request)
      const res = await clientsAPI.getAll({ limit: 100 });
      const allClients = res.data.clients || res.data || [];
      
      // Get existing client IDs in this flow (to mark them as enrolled)
      const existingIds = new Set(entries.map(e => e.client_id));
      
      // Include ALL clients but mark enrolled ones
      const clientsWithStatus = allClients.map(c => ({
        ...c,
        isEnrolled: existingIds.has(c.id)
      }));
      setAvailableClients(clientsWithStatus);
    } catch (error) {
    } finally {
      setLoadingClients(false);
    }
  };

  const searchClients = async () => {
    if (!searchTerm.trim()) return;
    
    setLoadingClients(true);
    try {
      const res = await clientsAPI.getAll({ search: searchTerm, limit: 100 });
      const allClients = res.data.clients || res.data || [];
      
      // Get existing client IDs in this flow (to mark them as enrolled)
      const existingIds = new Set(entries.map(e => e.client_id));
      
      // Include ALL clients but mark enrolled ones
      const clientsWithStatus = allClients.map(c => ({
        ...c,
        isEnrolled: existingIds.has(c.id)
      }));
      setAvailableClients(clientsWithStatus);
    } catch (error) {
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAddClients = async () => {
    if (selectedClients.length === 0) return;
    
    setAdding(true);
    try {
      const response = await waAPI.addClientsToFlow(flowId, selectedClients);
      const message = response.data.message || `Added ${response.data.added} clients`;
      alert(message);
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      alert('Failed to add clients');
    } finally {
      setAdding(false);
    }
  };

  const openGroupModal = async () => {
    setShowGroupModal(true);
    setLoadingGroups(true);
    setSelectedGroup(null);
    
    try {
      const res = await waAPI.getGroups();
      setGroups(res.data || []);
    } catch (error) {
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAddGroup = async () => {
    if (!selectedGroup) return;
    
    setAddingGroup(true);
    try {
      const response = await waAPI.addGroupToFlow(flowId, selectedGroup);
      const message = response.data.message || `Added clients from group`;
      alert(message);
      setShowGroupModal(false);
      fetchData();
    } catch (error) {
      alert('Failed to add group');
    } finally {
      setAddingGroup(false);
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const getDayLabel = (day) => {
    if (day === 1) return 'Day 1';
    if (day === 365) return 'Day 365 (1 Year)';
    return `Day ${day}`;
  };

  const getEntryStatusStyle = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      completed: 'bg-purple-100 text-purple-700',
      stopped: 'bg-red-100 text-red-700',
      paused: 'bg-yellow-100 text-yellow-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  // All Clients shows all entries (server-side filtered, excludes completed/stopped)
  const filteredEntries = entries;

  // Debounced entry search
  useEffect(() => {
    if (activeTab !== 'entries') return;
    const timer = setTimeout(() => {
      setEntriesPage(1);
      fetchEntries(1, entriesPerPage, entrySearchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [entrySearchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Flow not found</p>
        <Button onClick={() => navigate('/whatsapp/fixed-flows')} className="mt-4">
          Back to Fixed Flows
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp/fixed-flows')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{flow.name}</h1>
              <span className={`px-2 py-1 text-xs rounded-full ${
                flow.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {flow.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            {flow.description && (
              <p className="text-gray-500 mt-1">{flow.description}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline"
            onClick={openGroupModal}
            className="border-purple-200 text-purple-600 hover:bg-purple-50"
          >
            <UsersRound size={16} className="mr-2" />
            Add Client Group
          </Button>
          <Button 
            onClick={openAddClientsModal}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
          >
            <UserPlus size={16} className="mr-2" />
            Add Clients
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <Users className="text-gray-400" size={20} />
            <div>
              <p className="text-2xl font-bold text-gray-900">{flow.stats?.total_clients || 0}</p>
              <p className="text-sm text-gray-500">Total Enrolled</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <Play className="text-green-500" size={20} />
            <div>
              <p className="text-2xl font-bold text-green-600">{flow.stats?.active || 0}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Check className="text-purple-500" size={20} />
              <div>
                <p className="text-2xl font-bold text-purple-600">{flow.stats?.completed || 0}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
            {(flow.stats?.completed || 0) > 0 && (
              <button
                onClick={() => {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/wa/fixed-flows/${flowId}/export-completed`;
                  const token = localStorage.getItem('token');
                  fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
                    .then(res => res.blob())
                    .then(blob => {
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `completed_${flow.name?.replace(/\s/g, '_')}.csv`;
                      a.click();
                    });
                }}
                className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                title="Download completed as CSV"
                data-testid="export-completed-btn"
              >
                <Download size={16} className="text-purple-500" />
              </button>
            )}
          </div>
        </Card>
        <Card className="p-4 bg-white border border-gray-100">
          <div className="flex items-center gap-3">
            <StopCircle className="text-red-500" size={20} />
            <div>
              <p className="text-2xl font-bold text-red-600">{flow.stats?.stopped || 0}</p>
              <p className="text-sm text-gray-500">Stopped</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('queue')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'queue'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Today's Queue ({queue.length})
          </button>
          <button
            onClick={() => setActiveTab('entries')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'entries'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All Clients ({entriesTotalCount})
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'summary'
                ? 'border-[#4F46E5] text-[#4F46E5]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-summary"
          >
            Summary
          </button>
        </nav>
      </div>

      {/* Queue Tab */}
      {activeTab === 'queue' && (
        <Card className="bg-white border border-gray-100 overflow-hidden" data-testid="flow-queue-card">
          {queue.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Messages Due Today</h3>
              <p className="text-gray-500 mb-6">
                {entries.length === 0 
                  ? "No clients enrolled yet. Add clients to start the flow."
                  : "Messages will appear here when they're due for sending"
                }
              </p>
              {entries.length === 0 && (
                <div className="flex justify-center gap-3">
                  <Button variant="outline" onClick={openGroupModal} className="border-purple-200 text-purple-600 hover:bg-purple-50">
                    <UsersRound size={16} className="mr-2" /> Add Client Group
                  </Button>
                  <Button onClick={openAddClientsModal} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
                    <UserPlus size={16} className="mr-2" /> Add Clients
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Filter pills + Refresh */}
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setQueueFilter('all'); setQueuePage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      queueFilter === 'all' ? 'bg-[#4F46E5] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All ({queue.length})
                  </button>
                  <button
                    onClick={() => { setQueueFilter('pending'); setQueuePage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      queueFilter === 'pending' ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    Pending ({queue.filter(q => !q.is_overdue).length})
                  </button>
                  <button
                    onClick={() => { setQueueFilter('overdue'); setQueuePage(1); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      queueFilter === 'overdue' ? 'bg-yellow-500 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                    }`}
                  >
                    Overdue ({queue.filter(q => q.is_overdue).length})
                  </button>
                </div>
                <Button variant="outline" size="sm" onClick={handleRefresh}>
                  <RefreshCw size={14} className="mr-2" /> Refresh
                </Button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto" data-testid="flow-queue-table">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Day</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedQueue.map((item, index) => {
                      const statusInfo = getStatusBadge(item);
                      const hasTrackingError = !!item.tracking_skip_reason;
                      return (
                        <tr key={item.entry_id} className={`hover:bg-gray-50 ${hasTrackingError ? 'bg-amber-50/50' : ''}`} data-testid={`flow-queue-row-${item.entry_id}`}>
                          <td className="px-4 py-4 text-gray-500">{(queuePage - 1) * QUEUE_PAGE_SIZE + index + 1}</td>
                          <td className="px-4 py-4">
                            <p className="font-medium text-gray-900">{item.client_name}</p>
                            {hasTrackingError && (
                              <p className="text-xs text-amber-600 mt-0.5">{item.tracking_skip_reason}</p>
                            )}
                          </td>
                          <td className="px-4 py-4 text-gray-600">{item.client_phone}</td>
                          <td className="px-4 py-4">
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#4F46E5]/10 text-[#4F46E5]">
                              {getDayLabel(item.current_day)}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                              {statusInfo.icon} {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <Button variant="ghost" size="sm" onClick={() => openPreview(item)} className="text-gray-500">
                              <Eye size={14} className="mr-1" /> Preview
                            </Button>
                          </td>
                          <td className="px-4 py-4">
                            <TooltipProvider delayDuration={200}>
                              <div className="flex items-center gap-2">
                                {item.media_url && !item.media_url.includes('youtube') && !item.media_url.includes('youtu.be') && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        onClick={() => handleCopyImage(item.media_url, item.entry_id)}
                                        className={copiedImageId === item.entry_id ? 'text-green-600 border-green-200 bg-green-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}
                                      >
                                        {copiedImageId === item.entry_id ? (
                                          <><Check size={14} className="mr-1" /> Copied</>
                                        ) : (
                                          <><Copy size={14} className="mr-1" /> Copy Image</>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Copy image to clipboard — paste in WhatsApp chat</p></TooltipContent>
                                  </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <a href={item.wa_link} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#25D366] text-white text-sm rounded-lg hover:bg-[#20bd5a] transition-colors">
                                      <MessageSquare size={14} /> Open WhatsApp
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Open WhatsApp with pre-filled message</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="outline" size="sm" onClick={() => handleMarkSent(item.entry_id)} className="text-green-600 border-green-200 hover:bg-green-50">
                                      <CheckCircle size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Mark as Sent</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="outline" size="sm" onClick={() => { setSkipReasonModal({ entryId: item.entry_id }); setSkipReasonText(''); }} className="text-yellow-600 border-yellow-200 hover:bg-yellow-50">
                                      <SkipForward size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Skip this message</p></TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="outline" size="sm" onClick={() => handleStop(item.entry_id)} className="text-red-600 border-red-200 hover:bg-red-50">
                                      <StopCircle size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Stop this client's flow</p></TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Queue Pagination */}
              {totalQueuePages > 1 && (
                <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Page {queuePage} of {totalQueuePages} ({filteredQueue.length} total)
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQueuePage(p => Math.max(1, p - 1))}
                      disabled={queuePage === 1}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setQueuePage(p => Math.min(totalQueuePages, p + 1))}
                      disabled={queuePage === totalQueuePages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <Card className="bg-white border border-gray-100 overflow-hidden">
          {/* Header with search and per-page */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-900">All Clients</h3>
              <span className="text-sm text-gray-500">({entriesTotalCount} total)</span>
            </div>
            <div className="flex items-center gap-3">
              <select 
                value={entriesPerPage} 
                onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setEntriesPage(1); }}
                className="border border-gray-200 rounded-md px-2 py-1.5 text-sm text-gray-600 bg-white"
                data-testid="entries-per-page"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
              <Input placeholder="Search..." value={entrySearchTerm} onChange={(e) => setEntrySearchTerm(e.target.value)} className="w-48" />
            </div>
          </div>
          {entriesLoading ? (
            <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-[#4F46E5]" size={24} /></div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Clients Enrolled</h3>
              <p className="text-gray-500 mb-4">Add clients to this flow to start sending messages</p>
              <div className="flex justify-center gap-3">
                <Button 
                  variant="outline"
                  onClick={openGroupModal}
                  className="border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  <UsersRound size={16} className="mr-2" />
                  Add Client Group
                </Button>
                <Button 
                  onClick={openAddClientsModal}
                  className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                >
                  <UserPlus size={16} className="mr-2" />
                  Add Clients
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Bulk actions bar */}
              {selectedEntries.length > 0 && (
                <div className="p-3 bg-red-50 border-b border-red-100 flex items-center justify-between">
                  <span className="text-sm text-red-700 font-medium">
                    {selectedEntries.length} client{selectedEntries.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="bulk-delete-btn"
                  >
                    {bulkDeleting ? <Loader2 className="animate-spin mr-2" size={14} /> : <Trash2 size={14} className="mr-2" />}
                    Delete Selected
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntries(filteredEntries.map(entry => entry.id));
                            } else {
                              setSelectedEntries([]);
                            }
                          }}
                          className="rounded"
                          data-testid="select-all-entries"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Enrolled</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Progress</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Next Message</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEntries.map((entry) => {
                    const sentCount = entry.messages?.filter(m => m.status === 'sent').length || 0;
                    const skippedCount = entry.messages?.filter(m => m.status === 'skipped').length || 0;
                    const totalCount = entry.messages?.length || 0;
                    const sentPercent = totalCount > 0 ? (sentCount / totalCount) * 100 : 0;
                    const skippedPercent = totalCount > 0 ? (skippedCount / totalCount) * 100 : 0;
                    
                    return (
                      <tr key={entry.id} className={`hover:bg-gray-50 ${selectedEntries.includes(entry.id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry.id)}
                            onChange={() => toggleEntrySelection(entry.id)}
                            className="rounded"
                            data-testid={`select-entry-${entry.id}`}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-medium text-gray-900">{entry.client_name}</p>
                          <p className="text-sm text-gray-500">{entry.client_phone}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-600">
                            {formatDateMY(entry.enrolled_date)}
                          </p>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full capitalize ${getEntryStatusStyle(entry.status)}`}>
                            {entry.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2 overflow-hidden flex">
                              {skippedPercent > 0 && (
                                <div className="bg-amber-400 h-2" style={{ width: `${skippedPercent}%` }} />
                              )}
                              <div className="bg-[#4F46E5] h-2" style={{ width: `${sentPercent}%` }} />
                            </div>
                            <span className="text-xs text-gray-500">{sentCount + skippedCount}/{totalCount}</span>
                          </div>
                          {(() => {
                            const skippedMsgs = (entry.messages || []).filter(m => m.status === 'skipped' && m.skip_reason);
                            if (skippedMsgs.length === 0) return null;
                            return (
                              <div className="mt-1 space-y-0.5">
                                {skippedMsgs.map((m, i) => (
                                  <p key={i} className="text-[10px] text-amber-600">Day {m.day}: {m.skip_reason}</p>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-4">
                          {entry.next_message_day ? (
                            <div>
                              <p className="text-sm font-medium text-[#4F46E5]">
                                {getDayLabel(entry.next_message_day)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDateMY(entry.next_message_date)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1">
                            {entry.status === 'active' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRetryEntry(entry.id)}
                                  className="h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Send to Today's Queue"
                                  data-testid={`retry-entry-${entry.id}`}
                                >
                                  <RefreshCw size={14} />
                                </Button>
                                {skippedCount > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRetrySkipped(entry.id)}
                                    className="h-7 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    title={`Retry ${skippedCount} skipped message(s)`}
                                    data-testid={`retry-skipped-${entry.id}`}
                                  >
                                    <RotateCcw size={14} />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSkipReasonModal({ entryId: entry.id }); setSkipReasonText(''); }}
                                  className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Skip current message"
                                  data-testid={`skip-entry-${entry.id}`}
                                >
                                  <SkipForward size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleStop(entry.id)}
                                  className="h-7 px-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                  title="Pause - stop sending messages"
                                  data-testid={`stop-entry-${entry.id}`}
                                >
                                  <StopCircle size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Remove from flow"
                                  data-testid={`delete-entry-${entry.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                            {entry.status === 'stopped' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRetryEntry(entry.id)}
                                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Reactivate and send to Today's Queue"
                                  data-testid={`retry-stopped-${entry.id}`}
                                >
                                  <RefreshCw size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Remove from flow"
                                  data-testid={`delete-stopped-${entry.id}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                            {entry.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="h-7 px-2 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                title="Remove from flow"
                                data-testid={`delete-completed-${entry.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
          {/* Pagination Controls */}
          {entriesTotalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {entriesPage} of {entriesTotalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEntriesPage(p => Math.max(1, p - 1))} disabled={entriesPage === 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEntriesPage(p => Math.min(entriesTotalPages, p + 1))} disabled={entriesPage === entriesTotalPages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <Card className="bg-white border border-gray-100" data-testid="flow-summary-card">
          {summaryLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="animate-spin mx-auto text-[#4F46E5] mb-3" size={32} />
              <p className="text-gray-500">Loading summary...</p>
            </div>
          ) : !summary ? (
            <div className="p-12 text-center text-gray-500">No data available</div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Sent Overview */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Messages Sent</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-green-700">{summary.sent_today}</p>
                    <p className="text-xs text-green-600 mt-1">Today</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-blue-700">{summary.sent_this_week}</p>
                    <p className="text-xs text-blue-600 mt-1">This Week</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-purple-700">{summary.sent_this_month}</p>
                    <p className="text-xs text-purple-600 mt-1">This Month</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-gray-700">{summary.sent_total}</p>
                    <p className="text-xs text-gray-500 mt-1">All Time</p>
                  </div>
                </div>
              </div>

              {/* Repurchase */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wider">Repurchase Tracking (After Day 30)</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-emerald-700">{summary.repurchased_count}</p>
                    <p className="text-xs text-emerald-600 mt-1">Clients Repurchased</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-amber-700">{summary.repurchase_eligible || 0}</p>
                    <p className="text-xs text-amber-600 mt-1">Day 30 Sent (Eligible)</p>
                  </div>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                    <p className="text-2xl font-bold text-indigo-700">{summary.repurchase_rate}%</p>
                    <p className="text-xs text-indigo-600 mt-1">Repurchase Rate</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Only counts clients who received the Day 30 message and then placed a new order after that date.</p>
              </div>

              {/* Per-Day Breakdown */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Per-Day Breakdown</h3>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setBreakdownPeriod('week')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        breakdownPeriod === 'week' ? 'bg-[#4F46E5] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid="breakdown-week-btn"
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => setBreakdownPeriod('all')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        breakdownPeriod === 'all' ? 'bg-[#4F46E5] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid="breakdown-all-btn"
                    >
                      All Time
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Day</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-green-600 uppercase">Sent</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-amber-600 uppercase">Skipped</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-blue-600 uppercase">Pending</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-red-600 uppercase">Failed</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Sent %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(breakdownPeriod === 'week' ? (summary.per_day_week || []) : summary.per_day).map((d) => {
                        const sentPct = d.total > 0 ? Math.round(d.sent / d.total * 100) : 0;
                        return (
                          <tr key={d.day} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-[#4F46E5]">Day {d.day}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{d.sent}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{d.skipped}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{d.pending}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{d.failed}</span>
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-600">{d.total}</td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                  <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${sentPct}%` }} />
                                </div>
                                <span className="text-xs text-gray-500">{sentPct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Skipped info removed per user request */}
            </div>
          )}
        </Card>
      )}

      {/* Add Clients Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Clients to Flow</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchClients()}
              />
              <Button onClick={searchClients} disabled={loadingClients} className="bg-gray-900 hover:bg-gray-800 text-white">
                <Search size={16} className="mr-2" />
                Search
              </Button>
            </div>
            
            {/* Client List */}
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
                </div>
              ) : availableClients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No clients found. Try searching.
                </div>
              ) : (
                <div className="divide-y">
                  {availableClients.map((client) => (
                    <label 
                      key={client.id}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${selectedClients.includes(client.id) ? 'bg-indigo-50' : ''} ${client.isEnrolled ? 'bg-blue-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 truncate">{client.name}</p>
                          {client.isEnrolled && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                              Already enrolled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{client.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-gray-500">
                {selectedClients.length} client{selectedClients.length !== 1 ? 's' : ''} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddClients}
                  disabled={selectedClients.length === 0 || adding}
                  className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                >
                  {adding ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : null}
                  Add Selected
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Group Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Client Group to Flow</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Select a group to add all its members to this flow. Already enrolled clients will be restarted from Day 1.
            </p>
            
            {loadingGroups ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UsersRound className="mx-auto text-gray-300 mb-3" size={40} />
                <p>No groups found</p>
                <p className="text-xs mt-1">Create a group in WhatsApp Broadcast → Groups first</p>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {groups.map((group) => (
                  <label 
                    key={group.id}
                    className={`flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer ${selectedGroup === group.id ? 'bg-purple-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="group"
                      checked={selectedGroup === group.id}
                      onChange={() => setSelectedGroup(group.id)}
                      className="text-purple-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{group.name}</p>
                      <p className="text-sm text-gray-500">
                        {group.member_count || group.members?.length || 0} members
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowGroupModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddGroup}
                disabled={!selectedGroup || addingGroup}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {addingGroup ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <UsersRound size={16} className="mr-2" />
                )}
                Add Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Preview - {previewItem?.client_name}</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="px-2 py-0.5 text-xs rounded-full bg-[#4F46E5]/10 text-[#4F46E5]">{getDayLabel(previewItem.current_day)}</span>
                <span>Due: {formatDateWithWeekdayMY(previewItem.due_date)}</span>
              </div>
              {previewItem.media_url && !previewItem.media_url.includes('youtube') && !previewItem.media_url.includes('youtu.be') && (
                <div className="cursor-pointer" onClick={() => { setShowPreview(false); setImagePreviewUrl(previewItem.media_url); }}>
                  <img src={previewItem.media_url} alt="Media" className="max-h-48 rounded-lg shadow-sm hover:opacity-90 transition-opacity"
                    onError={(e) => e.target.style.display = 'none'} />
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{previewItem.message_content}</p>
                {previewItem.media_url && previewItem.show_media_url && (
                  <p className="text-xs text-blue-600 mt-2 break-all">{previewItem.media_url}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-size Image Preview Modal */}
      {imagePreviewUrl && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImagePreviewUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setImagePreviewUrl(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={imagePreviewUrl} 
            alt="Full size preview" 
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Skip Reason Modal */}
      <Dialog open={!!skipReasonModal} onOpenChange={(open) => { if (!open) { setSkipReasonModal(null); setSkipReasonText(''); } }}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Skip Message — Reason
            </h3>
            <div className="space-y-2">
              {SKIP_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setSkipReasonText(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    skipReasonText === r ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                  }`}
                  data-testid={`skip-reason-${r.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Input
              placeholder="Or type a custom reason..."
              value={SKIP_REASONS.includes(skipReasonText) ? '' : skipReasonText}
              onChange={(e) => setSkipReasonText(e.target.value)}
              data-testid="skip-reason-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { setSkipReasonModal(null); setSkipReasonText(''); }}>Cancel</Button>
              <Button
                size="sm"
                onClick={submitSkipReason}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="submit-skip-reason-btn"
              >
                Skip
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WAFixedFlowDetailPage;
